import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Mail, Lock, Eye, EyeOff, ArrowRight, Loader2, LogOut, User, Phone, Image as ImageIcon, CheckCircle, ShieldAlert } from 'lucide-react';
import { useUser } from '../contexts/UserContext';
import { useNotification } from './Notification';
import {
  auth, googleProvider, signInWithPopup, signInWithEmailAndPassword,
  signInAnonymously, db, setDoc, doc, getDoc,
  createUserWithEmailAndPassword, sendEmailVerification,
  updateProfile, serverTimestamp,
  linkWithCredential, EmailAuthProvider, PhoneAuthProvider, signInWithCredential, linkWithPopup
} from '../firebase';
import { GoogleAuthProvider } from 'firebase/auth';
import { GoogleAuth } from '@codetrix-studio/capacitor-google-auth';
import { Capacitor } from '@capacitor/core';

interface LoginFormProps {
  isDark: boolean;
  onLoginSuccess?: () => void;
}

interface UserData {
  name: string;
  email: string;
  picture: string;
}

export const LoginForm: React.FC<LoginFormProps> = ({ isDark, onLoginSuccess }) => {
  const { user: contextUser } = useUser();
  const { showNotification } = useNotification();
  const navigate = useNavigate();
  const [isLoginMode, setIsLoginMode] = useState(true);
  const [showGoogleTerms, setShowGoogleTerms] = useState(false);
  const [pendingGoogleUser, setPendingGoogleUser] = useState<any>(null);

  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [isEmailLoading, setIsEmailLoading] = useState(false);
  const [isGuestLoading, setIsGuestLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // Form State
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [profileImage, setProfileImage] = useState('');

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) {
      GoogleAuth.initialize({
        clientId: '877881200792-g5v82bov7n4k5p3c2p42f6m1h4g82s7k.apps.googleusercontent.com',
        scopes: ['profile', 'email'],
        grantOfflineAccess: true,
      });
    }
  }, []); // Could add actual upload logic if needed, keeping simple for now


  const checkPasswordStrength = (pass: string) => {
    let strength = 0;
    if (pass.length >= 8) strength += 1;
    if (/[a-z]/.test(pass) && /[A-Z]/.test(pass)) strength += 1;
    if (/\d/.test(pass)) strength += 1;
    if (/[^a-zA-Z\d]/.test(pass)) strength += 1;

    if (pass.length === 0) return { label: '', color: '' };
    if (strength < 2) return { label: 'ضعيفة', color: 'text-red-500' };
    if (strength < 4) return { label: 'متوسطة', color: 'text-yellow-500' };
    return { label: 'قوية', color: 'text-green-500' };
  };

  const getGeneratedAvatar = (name: string) => {
    const firstLetter = name ? name.charAt(0).toUpperCase() : 'U';
    const bgColors = ['e53935', 'd81b60', '8e24aa', '5e35b1', '3949ab', '1e88e5', '039be5', '00acc1', '00897b', '43a047', 'f4511e'];
    const randomColor = bgColors[Math.floor(Math.random() * bgColors.length)];
    return `https://ui-avatars.com/api/?name=${firstLetter}&background=${randomColor}&color=fff&size=128`;
  };

  const createFirestoreUser = async (uid: string, data: any) => {
    const finalPhotoUrl = profileImage || data.photoURL || getGeneratedAvatar(data.name || 'User');
    await setDoc(doc(db, "users", uid), {
      uid,
      name: data.name || '',
      email: data.email || '',
      phone: data.phone || '',
      photoURL: finalPhotoUrl,
      role: data.role || "user",
      isGuest: data.isGuest || false,
      agreedToTerms: true,
      createdAt: serverTimestamp(),
      lastLogin: serverTimestamp()
    }, { merge: true });
  };

  const handleAuthAction = async () => {
    if (isLoginMode) {
      if (!email || !password) {
        showNotification("الرجاء إدخال البريد الإلكتروني وكلمة المرور", "warning");
        return;
      }
      setIsEmailLoading(true);
      try {
        let finalEmail = email.trim();
        // If it's a phone number (11 digits starting with 01), convert to dummy email
        if (/^01[0-9]{9}$/.test(finalEmail)) {
          finalEmail = `${finalEmail}@rashid-online.com`;
        }

        if (auth.currentUser?.isAnonymous) {
          const credential = EmailAuthProvider.credential(finalEmail, password);
          const userCredential = await linkWithCredential(auth.currentUser, credential);
          const user = userCredential.user;
          await createFirestoreUser(user.uid, { email: user.email || finalEmail, name: 'User', isGuest: false, role: 'user' });
          onLoginSuccess?.();
        } else {
          const userCredential = await signInWithEmailAndPassword(auth, finalEmail, password);
          const user = userCredential.user;

          if (!user.emailVerified) {
            await sendEmailVerification(user);
            await auth.signOut();
            showNotification(
              <div>
                يرجى تفعيل بريدك الإلكتروني أولاً قبل تسجيل الدخول. تم إرسال رابط تفعيل جديد لبريدك الآن (يرجى التحقق من البريد الوارد وقسم الـ Spam/Junk).
                <br />
                <button 
                  onClick={() => window.open('https://wa.me/201202796965', '_blank')}
                  className="mt-2 w-full py-1.5 px-3 bg-[#25D366] text-white text-xs font-black rounded-lg hover:bg-green-600 transition-colors shadow-sm"
                >
                  لدي مشكلة
                </button>
              </div>,
              "warning",
              10000 // Show longer for actionable button
            );
            setIsEmailLoading(false);
            return;
          }

          const userRef = doc(db, "users", user.uid);
          const userSnap = await getDoc(userRef);

          if (userSnap.exists()) {
            await setDoc(userRef, { lastLogin: serverTimestamp() }, { merge: true });
          } else {
            await createFirestoreUser(user.uid, { email: user.email, name: 'User' });
          }
          onLoginSuccess?.();
        }
      } catch (error: any) {
        console.error("Login Error:", error);
        if (error.code === 'auth/invalid-credential' || error.code === 'auth/user-not-found' || error.code === 'auth/wrong-password') {
          showNotification("البريد الإلكتروني أو كلمة المرور غير صحيحة", "error");
        } else {
          showNotification("حدث خطأ أثناء تسجيل الدخول: " + error.message, "error");
        }
      } finally {
        setIsEmailLoading(false);
      }
    } else {
      // Register Mode
      if (!fullName) return showNotification("الاسم الكامل مطلوب", "warning");
      if (!email) return showNotification("البريد الإلكتروني مطلوب لإنشاء الحساب", "warning");
      if (password !== confirmPassword) return showNotification("كلمات المرور غير متطابقة", "warning");
      if (!agreedToTerms) return showNotification("يجب الموافقة على الشروط وسياسة الاستخدام", "warning");

      setIsEmailLoading(true);
      try {
        let finalEmail = email.trim();
        if (/^01[0-9]{9}$/.test(finalEmail)) {
          finalEmail = `${finalEmail}@rashid-online.com`;
        }

        if (finalEmail) {
          // Email Auth
          if (auth.currentUser?.isAnonymous) {
            const credential = EmailAuthProvider.credential(finalEmail, password);
            const userCredential = await linkWithCredential(auth.currentUser, credential);
            await updateProfile(userCredential.user, { displayName: fullName });
            await sendEmailVerification(userCredential.user);
            await createFirestoreUser(userCredential.user.uid, { name: fullName, email: finalEmail, phone, isGuest: false, role: 'user' });
          } else {
            const userCredential = await createUserWithEmailAndPassword(auth, finalEmail, password);
            await updateProfile(userCredential.user, { displayName: fullName });
            await sendEmailVerification(userCredential.user);
            await createFirestoreUser(userCredential.user.uid, { name: fullName, email: finalEmail, phone });
          }

          showNotification("تم إنشاء الحساب بنجاح. يرجى تفعيل البريد الإلكتروني قبل الاستخدام الكامل.", "success");
          onLoginSuccess?.();
        }
      } catch (error: any) {
        console.error("Registration Error:", error);
        if (error.code === 'auth/email-already-in-use') {
          showNotification("البريد الإلكتروني مستخدم بالفعل.", "error");
        } else if (error.code === 'auth/invalid-phone-number') {
          showNotification("رقم الهاتف غير صالح. يرجى إدخال رقم هاتف صحيح مع رمز الدولة (مثال: +201...)", "error");
        } else {
          showNotification("حدث خطأ أثناء التسجيل: " + error.message, "error");
        }
      } finally {
        setIsEmailLoading(false);
      }
    }
  };

  const handleGoogleLogin = async () => {
    setIsGoogleLoading(true);
    try {
      let user;
      if (auth.currentUser?.isAnonymous) {
        const result = await linkWithPopup(auth.currentUser, googleProvider);
        user = result.user;
        // When upgrading, they haven't agreed to terms yet (or they have as guest, but we force it for clarity)
        setPendingGoogleUser(user);
        setShowGoogleTerms(true);
        return;
      } else {
        let result;
        if (Capacitor.isNativePlatform()) {
          const googleUser = await GoogleAuth.signIn();
          if (googleUser && googleUser.authentication && googleUser.authentication.idToken) {
            const credential = GoogleAuthProvider.credential(googleUser.authentication.idToken);
            result = await signInWithCredential(auth, credential);
          } else {
            throw new Error("No ID Token provided by Google");
          }
        } else {
          result = await signInWithPopup(auth, googleProvider);
        }
        user = result.user;
      }

      const userRef = doc(db, "users", user.uid);
      const userSnap = await getDoc(userRef);

      if (!userSnap.exists() || !userSnap.data()?.agreedToTerms) {
        setPendingGoogleUser(user);
        setShowGoogleTerms(true);
      } else {
        await setDoc(userRef, { lastLogin: serverTimestamp() }, { merge: true });
        onLoginSuccess?.();
      }
    } catch (error: any) {
      console.error("Google Login Error:", error);
      if (error.code !== 'auth/popup-closed-by-user') {
        showNotification("حدث خطأ أثناء تسجيل الدخول بواسطة جوجل: " + error.message, "error");
      }
    } finally {
      setIsGoogleLoading(false);
    }
  };

  const handleConfirmGoogleTerms = async () => {
    if (!agreedToTerms) return showNotification("يجب الموافقة على الشروط للمتابعة", "warning");
    if (!pendingGoogleUser) return;

    setIsGoogleLoading(true);
    try {
      await createFirestoreUser(pendingGoogleUser.uid, {
        name: pendingGoogleUser.displayName,
        email: pendingGoogleUser.email,
        photoURL: pendingGoogleUser.photoURL,
        provider: "google"
      });
      setShowGoogleTerms(false);
      onLoginSuccess?.();
    } catch (error) {
      console.error(error);
    } finally {
      setIsGoogleLoading(false);
    }
  };

  const handleGuestLogin = async () => {
    if (isGuestLoading) return;
    setIsGuestLoading(true);
    try {
      const userCredential = await signInAnonymously(auth);
      const guestUser = userCredential.user;

      const userRef = doc(db, "users", guestUser.uid);
      const userSnap = await getDoc(userRef);
      if (!userSnap.exists()) {
        await createFirestoreUser(guestUser.uid, {
          name: "Guest_" + Math.floor(Math.random() * 1000000),
          role: "guest",
          isGuest: true
        });
      }
      onLoginSuccess?.();
      navigate('/'); // Redirect to home immediately as requested
    } catch (error: any) {
      console.error("Guest sign-in error:", error);
      showNotification("حدث خطأ أثناء تسجيل دخول الضيف. يرجى المحاولة مرة أخرى.", "error");
    } finally {
      setIsGuestLoading(false);
    }
  };

  const handleFacebookLogin = () => {
    showNotification("قريباً... يرجى استخدام جوجل حالياً", "info");
  };


  // Styles
  const containerBg = isDark ? 'bg-black/20' : 'bg-white/20';
  const inputBg = isDark ? 'bg-black/30' : 'bg-white/50';
  const inputClass = `w-full h-10 md:h-11 pr-10 md:pr-12 pl-10 md:pl-12 rounded-2xl ${inputBg} border border-white/30 ${isDark ? 'text-white placeholder:text-white/40' : 'text-blue-950 placeholder:text-blue-900/60'} focus:outline-none focus:ring-2 focus:ring-blue-400 text-center text-sm md:text-md transition-all shadow-sm font-bold`;

  const strength = checkPasswordStrength(password);

  return (
    <div className={`w-full p-4 md:p-5 rounded-[1.5rem] md:rounded-[2rem] shadow-2xl transition-all duration-700 backdrop-blur-xl border ${containerBg} border-white/20 flex flex-col space-y-2 md:space-y-3 relative overflow-y-auto no-scrollbar max-h-[85vh]`}>

      {showGoogleTerms ? (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setShowGoogleTerms(false)}></div>
          <div className={`relative w-full max-w-lg max-h-[85vh] rounded-[2.5rem] shadow-2xl flex flex-col items-center justify-start p-6 text-center animate-in zoom-in duration-300 overflow-y-auto custom-scrollbar ${isDark ? 'bg-[#121418] border border-white/10 text-white' : 'bg-white border text-slate-800'}`}>
            <ShieldAlert size={48} className={`mb-4 ${isDark ? 'text-blue-400' : 'text-blue-600'} shrink-0`} />
            <h3 className={`text-xl font-black mb-2 shrink-0`}>مرحباً بك في Rashid Online</h3>
            <p className={`text-sm mb-6 shrink-0 opacity-80 ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>لإكمال تسجيل الدخول والاستفادة من كافة خدماتنا، يرجى الموافقة على شروط الاستخدام وسياسة الخصوصية الخاصة بنا.</p>

            <div className={`w-full p-4 rounded-2xl mb-6 text-right text-xs leading-relaxed border flex-1 overflow-y-auto custom-scrollbar ${isDark ? 'bg-white/5 border-white/10 text-slate-300' : 'bg-slate-50 border-slate-200 text-slate-600'}`}>
              <p className="font-bold mb-2 underline text-blue-500">سياسة الاستخدام:</p>
              <ul className="list-disc pr-4 space-y-2">
                <li>الالتزام بكافة القوانين واللوائح المعمول بها في جمهورية مصر العربية.</li>
                <li>استخدام التطبيق للأغراض الشخصية فقط وعدم إساءة الاستخدام أو استغلال الثغرات.</li>
                <li>الحفاظ على سرية بيانات حسابك وعدم مشاركتها مع أطراف خارجية، لتجنب الاحتيال.</li>
                <li>الموافقة على استقبال إشعارات وتحديثات الطلبات لتسهيل التوصيل والتشغيل.</li>
                <li>يجب أن تكون المعلومات المقدمة دقيقة، لضمان استلام طلباتك في الوقت والمكان المحددين.</li>
              </ul>
              <p className="mt-4 text-[11px] opacity-70">بالنقر على إتمام التسجيل، فإنك تقر وتوافق على كامل بنود الخدمة.</p>
            </div>

            <label className="flex items-center justify-center gap-2 mb-6 cursor-pointer group shrink-0 w-full">
              <input
                type="checkbox"
                checked={agreedToTerms}
                onChange={e => setAgreedToTerms(e.target.checked)}
                className="w-5 h-5 rounded border-gray-300 cursor-pointer accent-blue-600"
              />
              <span className={`text-sm font-bold ${isDark ? 'text-white' : 'text-blue-950'} group-hover:text-blue-500 transition-colors`}>أوافق على الشروط وسياسة الاستخدام</span>
            </label>

            <button
              onClick={handleConfirmGoogleTerms}
              disabled={isGoogleLoading}
              className="w-full shrink-0 py-4 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-2xl font-black shadow-xl flex items-center justify-center active:scale-95 transition-all text-lg"
            >
              {isGoogleLoading ? <Loader2 size={24} className="animate-spin" /> : "إتمام التسجيل"}
            </button>

            <button
              onClick={() => setShowGoogleTerms(false)}
              className={`mt-4 shrink-0 text-sm font-bold ${isDark ? 'text-white/40 hover:text-white' : 'text-slate-500 hover:text-slate-800'} transition-colors`}
            >
              إلغاء والعودة
            </button>
          </div>
        </div>
      ) : (
        <div className="flex flex-col space-y-2 md:space-y-3">

          {!isLoginMode && (
            <>
              <div className="relative group animate-in fade-in slide-in-from-top-2 duration-300">
                <div className={`absolute inset-y-0 right-0 pr-3 md:pr-4 flex items-center pointer-events-none ${isDark ? 'text-white/60' : 'text-blue-900/70'}`}>
                  <User size={16} />
                </div>
                <input type="text" placeholder="الاسم" className={inputClass} value={fullName} onChange={(e) => setFullName(e.target.value)} />
              </div>

              <div className="relative group animate-in fade-in slide-in-from-top-2 duration-300 delay-75">
                <div className={`absolute inset-y-0 right-0 pr-3 md:pr-4 flex items-center pointer-events-none ${isDark ? 'text-white/60' : 'text-blue-900/70'}`}>
                  <Phone size={16} />
                </div>
                <input type="tel" placeholder="رقم الهاتف (اختياري - للتواصل)" className={inputClass} value={phone} onChange={(e) => setPhone(e.target.value)} />
              </div>
            </>
          )}

          <div className="relative group animate-in fade-in slide-in-from-top-2 duration-300">
            <div className={`absolute inset-y-0 right-0 pr-3 md:pr-4 flex items-center pointer-events-none ${isDark ? 'text-white/60' : 'text-blue-900/70'}`}>
              <Mail size={16} />
            </div>
            <input
              type="text"
              placeholder="البريد الالكتروني (مطلوب للتسجيل)"
              className={inputClass}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>

          <div className="relative group">
            <div className={`absolute inset-y-0 right-0 pr-3 md:pr-4 flex items-center pointer-events-none ${isDark ? 'text-white/60' : 'text-blue-900/70'}`}>
              <Lock size={16} />
            </div>
            <input
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="كلمة المرور"
              className={inputClass}
            />
            {password.length > 0 && (
              <button type="button" onClick={() => setShowPassword(!showPassword)} className={`absolute inset-y-0 left-0 pl-3 md:pl-4 flex items-center transition-all ${isDark ? 'text-white/50 hover:text-white' : 'text-blue-900/60 hover:text-blue-950'}`}>
                {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            )}
            {!isLoginMode && password.length > 0 && (
              <div className="absolute -bottom-[14px] right-2 text-[10px] font-bold transition-all animate-in fade-in">
                قوة الكلمة: <span className={strength.color}>{strength.label}</span>
              </div>
            )}
          </div>

          {!isLoginMode && (
            <>
              <div className="relative group mt-2 animate-in fade-in slide-in-from-top-2 duration-300 delay-150">
                <div className={`absolute inset-y-0 right-0 pr-3 md:pr-4 flex items-center pointer-events-none ${isDark ? 'text-white/60' : 'text-blue-900/70'}`}>
                  <Lock size={16} />
                </div>
                <input type={showPassword ? "text" : "password"} placeholder="تأكيد كلمة المرور" className={inputClass} value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} />
              </div>

              <label className="flex items-center gap-2 mt-1 px-2 cursor-pointer animate-in fade-in slide-in-from-top-2 duration-300 delay-200">
                <input type="checkbox" checked={agreedToTerms} onChange={e => setAgreedToTerms(e.target.checked)} className="w-3 h-3 md:w-4 md:h-4 rounded border-gray-300" />
                <span className={`text-[10px] md:text-xs font-bold ${isDark ? 'text-white' : 'text-blue-950'}`}>
                  أوافق على <a href="/policies" className="underline hover:text-blue-500">الشروط وسياسة الاستخدام</a>
                </span>
              </label>
            </>
          )}

          <button
            onClick={handleAuthAction}
            disabled={isEmailLoading}
            className={`w-full mt-1 h-10 md:h-12 bg-gradient-to-r ${!isLoginMode ? 'from-emerald-600 via-teal-600 to-emerald-600 shadow-emerald-500/20' : 'from-blue-600 via-indigo-600 to-blue-600 shadow-blue-500/20'} bg-[length:200%_auto] hover:bg-right text-white rounded-2xl font-black text-base md:text-lg shadow-xl active:scale-[0.98] transition-all duration-500 border border-white/20 flex items-center justify-center`}
          >
            {isEmailLoading ? <Loader2 className="animate-spin" /> : isLoginMode ? "تسجيل الدخول" : "إنشاء حساب"}
          </button>

          {isLoginMode && (
            <div className="text-right px-1">
              <button className={`text-[12px] md:text-[13px] font-black ${isDark ? 'text-cyan-300' : 'text-blue-900'} hover:underline drop-shadow-md brightness-110 opacity-100 transition-all`}>
                نسيت كلمة المرور؟
              </button>
            </div>
          )}

          <div className="relative flex items-center py-0.5 md:py-1">
            <div className="flex-grow flex items-center justify-center">
              <div className={`w-1/4 border-t-2 ${isDark ? 'border-white/30' : 'border-blue-900/40'}`}></div>
              <span className={`mx-2 md:mx-3 text-[12px] md:text-[14px] font-black ${isDark ? 'text-white' : 'text-blue-950'} drop-shadow-lg`}>أو</span>
              <div className={`w-1/4 border-t-2 ${isDark ? 'border-white/30' : 'border-blue-900/40'}`}></div>
            </div>
          </div>
          <center className="mt-6 md:mt-8 space-y-4">
            {isLoginMode && (() => {
              const googleLinked = (auth.currentUser || contextUser)?.providerData.find(p => p.providerId === 'google.com');
              if (googleLinked) {
                return (
                  <div className={`w-full h-auto p-3 rounded-2xl flex flex-col items-center justify-center space-y-1 transition-all shadow-md border ${isDark ? 'bg-emerald-500/10 border-emerald-500/20 text-emerald-400' : 'bg-emerald-50 border-emerald-200 text-emerald-700'}`}>
                    <div className="flex items-center space-x-2 space-x-reverse">
                      <CheckCircle size={16} />
                      <span className="font-black text-xs md:text-sm text-center">تم ربط حساب جوجل بنجاح ✅</span>
                    </div>
                    <span className={`text-[10px] md:text-xs font-bold opacity-80 ${isDark ? 'text-white/60' : 'text-slate-600'}`}>{googleLinked.email}</span>
                  </div>
                );
              }
              return (
                <button
                  onClick={handleGoogleLogin}
                  disabled={isGoogleLoading}
                  className={`w-full h-10 md:h-11 rounded-2xl flex items-center justify-center px-4 space-x-3 space-x-reverse transition-all active:scale-[0.98] shadow-md border ${isDark ? 'bg-white/10 border-white/20 text-white hover:bg-white/20' : 'bg-white border-white/50 text-blue-950 hover:bg-white'}`}
                >
                  {isGoogleLoading ? <Loader2 className="w-4 h-4 md:w-5 md:h-5 animate-spin" /> : <img src="https://www.gstatic.com/images/branding/product/1x/googleg_48dp.png" alt="Google" className="w-4 h-4 md:w-5 md:h-5" />}
                  <span className="font-black text-xs md:text-sm">ربط حساب جوجل (Gmail)</span>
                </button>
              );
            })()}
          </center>

          <div className="pt-1 flex flex-col md:flex-row items-center justify-center gap-2">
            {isLoginMode && (
              <button
                onClick={handleGuestLogin}
                disabled={isGuestLoading}
                className={`flex-1 px-4 py-2 rounded-xl ${isDark ? 'bg-blue-500/10 text-blue-400 border-blue-500/20' : 'bg-blue-50 text-blue-600 border-blue-200'} flex items-center justify-center font-black text-xs md:text-sm shadow-md border hover:scale-105 transition-transform disabled:opacity-50`}
              >
                {isGuestLoading ? <Loader2 size={14} className="animate-spin ml-2" /> : <User size={14} className="ml-2" />}
                <span>التسجيل كضيف</span>
              </button>
            )}

            <button
              onClick={() => setIsLoginMode(!isLoginMode)}
              className={`flex-1 px-4 py-2 rounded-xl w-full ${isDark ? 'bg-white/10 text-white border-white/30' : 'bg-white/80 text-blue-950 border-white/30'} flex items-center justify-center font-black text-xs md:text-sm shadow-md border hover:scale-105 transition-transform`}
            >
              <span>{isLoginMode ? "انشاء حساب" : "لدي حساب بالفعل"}</span>
              {isLoginMode && <ArrowRight size={14} className="mr-2 transform rotate-180" />}
            </button>
          </div>
        </div>
      )}
    </div >
  );
};
